import { Box, Button, Menu, MenuItem } from '@mui/material';
import { GridSortItem, GridSortModel } from '@mui/x-data-grid';
import { FilterChips } from 'components/common/FilterBar/FilterChips/FilterChips';
import {
    DataTable,
    DataTableColumn,
} from 'components/data/DataTable/DataTable';
import { useGlobalSearch } from 'hooks';
import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    EfficiencyAccount,
    useLazyGetAccountsQuery,
} from 'store/services/efficiency';
import styles from '../../Efficiency.module.scss';
import { KeyboardArrowDown } from '@mui/icons-material';

interface EfficiencyAccountsProps {
    supplierId?: string;
}

type PresetButton = {
    label: string;
    sortModel: GridSortItem;
};

export const EfficiencyAccounts = ({ supplierId }: EfficiencyAccountsProps) => {
    const [getAccountsQuery] = useLazyGetAccountsQuery();

    const { globalSearchTerm } = useGlobalSearch();

    const navigate = useNavigate();

    const [accounts, setAccounts] = useState<EfficiencyAccount[]>([]);
    const [searchResults, setSearchResults] = useState<EfficiencyAccount[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        { field: 'name', sort: 'asc' },
    ]);

    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const sortMenuButton = useRef<HTMLButtonElement>(null);

    const presetSorts: Record<string, PresetButton> = {
        mostEfficient: {
            label: 'Most Efficient',
            sortModel: { field: 'average', sort: 'desc' },
        },
        leastEfficient: {
            label: 'Least Efficient',
            sortModel: { field: 'average', sort: 'asc' },
        },
        mostAvoided: {
            label: 'Most Avoided',
            sortModel: { field: 'avoided', sort: 'desc' },
        },
        mostSaved: {
            label: 'Most Saved',
            sortModel: { field: 'saved', sort: 'desc' },
        },
    };

    // Load table data.
    useEffect(() => {
        if (supplierId) {
            setIsLoading(true);
            getAccountsQuery({ supplierId })
                .then((result) => {
                    if (result.data) {
                        setAccounts(result.data);
                        setSearchResults(result.data);
                    } else {
                        setAccounts([]);
                        setSearchResults([]);
                    }
                })
                .finally(() => setIsLoading(false));
        }
    }, [supplierId, getAccountsQuery]);

    const onSortChange = (model: GridSortModel) => {
        if (isEqual(model, sortModel) && model?.[0]?.sort) {
            return setSortModel([{ field: model?.[0]?.field, sort: null }]);
        }
        return setSortModel(model);
    };

    // Apply search term.
    useEffect(() => {
        if (globalSearchTerm) {
            setSearchResults((accts) => {
                return accts.filter((acct) =>
                    acct.name
                        ?.toLowerCase()
                        ?.includes(globalSearchTerm?.toLocaleLowerCase())
                );
            });
        } else {
            setSearchResults(accounts);
        }
    }, [globalSearchTerm, accounts]);

    return (
        <div data-testid="EfficiencyAccounts" style={{ height: '100%' }}>
            <DataTable
                id="efficiencyAccounts"
                data={searchResults}
                loading={isLoading}
                columns={efficiencyAccountsColumns}
                dataFormatter={(data) => data}
                onSortModelChange={(model) =>
                    onSortChange(model as GridSortModel)
                }
                sortModel={sortModel}
                csvOptions={{
                    fileName: `efficiency-accounts_supplier-${supplierId}`,
                }}
                onRowClick={(params) =>
                    navigate(`/device-details/${params.row.deviceId}`, {
                        state: params?.row,
                    })
                }
                getRowCount={(data) => data?.length ?? 0}
                toolbarComponents={
                    <Box sx={{ display: 'flex', gap: '0.5rem' }}>
                        <FilterChips filterGroups={[]} />
                        <div className={styles.prefabSorts}>
                            {Object.keys(presetSorts).map((model: string) => (
                                <Button
                                    key={`preset-sort-${model}`}
                                    title={`Sort By: ${presetSorts[model].label}`}
                                    onClick={() =>
                                        onSortChange([
                                            presetSorts[model].sortModel,
                                        ])
                                    }
                                    variant={
                                        isEqual(
                                            sortModel?.[0],
                                            presetSorts[model].sortModel
                                        )
                                            ? 'contained'
                                            : 'outlined'
                                    }
                                >
                                    {presetSorts[model].label}
                                </Button>
                            ))}
                        </div>
                        <div className={styles.prefabSortsMobile}>
                            <Button
                                variant="contained"
                                onClick={() =>
                                    setIsSortMenuOpen(
                                        (prevIsOpen: boolean) => !prevIsOpen
                                    )
                                }
                                endIcon={<KeyboardArrowDown />}
                                ref={sortMenuButton}
                            >
                                Sort
                            </Button>
                            <Menu
                                open={isSortMenuOpen}
                                anchorEl={sortMenuButton?.current}
                                onClose={() => setIsSortMenuOpen(false)}
                            >
                                {Object.keys(presetSorts).map(
                                    (model: string) => (
                                        <MenuItem
                                            key={`preset-sort-${model}`}
                                            title={`Sort By: ${presetSorts[model].label}`}
                                            selected={isEqual(
                                                sortModel?.[0],
                                                presetSorts[model].sortModel
                                            )}
                                            onClick={() => {
                                                onSortChange([
                                                    presetSorts[model]
                                                        .sortModel,
                                                ]);
                                                setIsSortMenuOpen(false);
                                            }}
                                        >
                                            {presetSorts[model].label}
                                        </MenuItem>
                                    )
                                )}
                            </Menu>
                        </div>
                    </Box>
                }
            />
        </div>
    );
};

const efficiencyAccountsColumns: DataTableColumn[] = [
    {
        headerName: 'Customer',
        field: 'customer_name',
    },
    {
        headerName: 'Tank Name',
        field: 'name',
        hideByDefault: true,
    },
    {
        headerName: 'Account Id',
        field: 'accountId',
    },
    {
        headerName: 'Delivery Count',
        field: 'count',
    },
    {
        headerName: 'Tank Size (gal)',
        field: 'capacity',
    },
    {
        headerName: 'Avg Delivery Size',
        field: 'average',
        valueFormatter: (params) => Math.round(params.value) + '%',
    },
    {
        headerName: 'Deliveries Avoided',
        field: 'avoided',
        valueFormatter: (params) => Math.round(params.value),
    },
    {
        headerName: 'Savings Missed',
        field: 'saved',
        valueFormatter: (params) => '$' + params.value,
    },
];
